/**
 * Reads the inline session model from `<script type="application/json"
 * id="session-model">` that serveSpaShell injects.
 *
 * This is the anti-flicker mechanism: the server resolves the session
 * cookie at SPA-shell serve time and writes a minimal JSON blob so the
 * client knows isLoggedIn on first paint - before the async
 * /api/auth/session round-trip completes. Without it, a logged-in user
 * refreshing /profile sees the visitor loginModal flash for 1-2s.
 *
 * The JSON is inert (application/json), CSP-safe, and viewer-specific
 * (the shell is already cache-control: no-store).
 *
 * Shape is intentionally limited to first-paint access state:
 *   { viewer: { authStatus, plan } }
 * Full viewer identity is populated only from /api/auth/session hydration.
 */

import type { PlanCode } from './apiContracts';

export interface InlineSessionViewer {
	authStatus: "authenticated";
	plan: PlanCode;
	avatarInitials: string;
}

export interface InlineSessionModel {
	viewer: InlineSessionViewer;
}

const planCodes = new Set<PlanCode>(['visitor', 'free', 'lite', 'pro', 'max']);

export function readInlineSessionModel(): InlineSessionModel | null {
	if (typeof document === "undefined") return null;
	const script = document.getElementById("session-model");
	if (!script?.textContent) return null;
	try {
		const parsed = JSON.parse(script.textContent) as { viewer?: { authStatus?: unknown; plan?: unknown; avatarInitials?: unknown } };
		if (parsed.viewer?.authStatus !== 'authenticated' || typeof parsed.viewer.plan !== 'string' || !planCodes.has(parsed.viewer.plan as PlanCode)) return null;
		if (typeof parsed.viewer.avatarInitials !== 'string' || !/^[A-Z0-9?]{1,2}$/.test(parsed.viewer.avatarInitials)) return null;
		return { viewer: { authStatus: 'authenticated', plan: parsed.viewer.plan as PlanCode, avatarInitials: parsed.viewer.avatarInitials } };
	} catch {
		return null;
	}
}
